"use client";

import { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import NavigationArrow from "@/assets/NavigationArrow";
import { cn } from "@/lib/utils";

type Props = {
  children: ReactNode;
  options?: EmblaOptionsType;
  hideButtons?: boolean;
  left_navigation_icon?: string;
  left_navigation_icon_alt?: string;
  right_navigation_icon?: string;
  right_navigation_icon_alt?: string;
  onNavigationClick?: (
    direction: "prev" | "next",
    currentIndex: number
  ) => void;
};

const TWEEN_FACTOR_BASE = 0.5;

const numberWithinRange = (number: number, min: number, max: number) =>
  Math.min(Math.max(number, min), max);

const EmblaCarousel: React.FC<Props> = ({
  children,
  options,
  hideButtons,
  left_navigation_icon,
  left_navigation_icon_alt,
  right_navigation_icon,
  right_navigation_icon_alt,
  onNavigationClick,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    loop: true,
    ...options,
  });

  const [isMobile, setIsMobile] = useState(false);
  const tweenFactor = useRef(0);
  const tweenNodes = useRef<HTMLDivElement[]>([]);

  const setTweenNodes = useCallback(() => {
    if (!emblaApi) return;
    tweenNodes.current = emblaApi
      .slideNodes()
      .map(
        (slideNode: HTMLElement) =>
          slideNode.querySelector(".carousel3__slide__card") as HTMLDivElement
      );
  }, [emblaApi]);

  const setTweenFactor = useCallback(() => {
    if (!emblaApi) return;
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length;
  }, [emblaApi]);

  const tweenScale = useCallback(() => {
    if (!emblaApi) return;
    const engine = emblaApi.internalEngine();
    const scrollProgress = emblaApi.scrollProgress();
    const isMobile = window.innerWidth < 768; // md breakpoint

    emblaApi.scrollSnapList().forEach((snap: number, snapIndex: number) => {
      const slidesInSnap = engine.slideRegistry[snapIndex];

      slidesInSnap.forEach((slideIndex: number) => {
        const slideNode = tweenNodes.current[slideIndex];
        if (!slideNode) return;

        let diffToTarget = snap - scrollProgress;

        if (engine.options.loop) {
          engine.slideLooper.loopPoints.forEach((loopItem: any) => {
            const target = loopItem.target();
            if (slideIndex === loopItem.index && target !== 0) {
              const sign = Math.sign(target);
              if (sign === -1) diffToTarget = snap - (1 + scrollProgress);
              if (sign === 1) diffToTarget = snap + (1 - scrollProgress);
            }
          });
        }

        const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current);

        // Apply scale and opacity only on mobile
        const scale = isMobile ? numberWithinRange(tweenValue, 0.85, 1.15) : 1;
        const opacity = isMobile ? numberWithinRange(tweenValue, 0.6, 1) : 1;

        slideNode.style.transform = `scale(${scale})`;
        slideNode.style.opacity = `${opacity}`;
        slideNode.style.transition =
          "transform 0.3s ease-out, opacity 0.3s ease-out";
      });
    });
  }, [emblaApi]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    setTweenNodes();
    setTweenFactor();
    tweenScale();
    setCurrentIndex(emblaApi.selectedScrollSnap());

    emblaApi
      .on("reInit", () => {
        setTweenNodes();
        setTweenFactor();
        tweenScale();
        setCurrentIndex(emblaApi.selectedScrollSnap());
      })
      .on("scroll", () => tweenScale())
      .on("select", () => {
        tweenScale();
        setCurrentIndex(emblaApi.selectedScrollSnap());
      });
  }, [emblaApi, setTweenNodes, setTweenFactor, tweenScale]);

  const [currentIndex, setCurrentIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
      const newIndex = emblaApi.selectedScrollSnap();
      setCurrentIndex(newIndex);
      onNavigationClick?.("prev", newIndex);
    }
  }, [emblaApi, onNavigationClick]);

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
      const newIndex = emblaApi.selectedScrollSnap();
      setCurrentIndex(newIndex);
      onNavigationClick?.("next", newIndex);
    }
  }, [emblaApi, onNavigationClick]);

  return (
    <div className="relative w-full max-w-full overflow-hidden">
      <div className="overflow-hidden" ref={emblaRef}>
        <div
          className={cn("flex gap-4 md:gap-5 py-6 md:py-3")}
          style={{
            paddingLeft: isMobile ? "16px" : "20%",
            paddingRight: isMobile ? "16px" : "20%",
          }}
        >
          {React.Children.map(children, (child, index) => {
            return (
              <div
                key={index}
                className="min-w-0"
                style={{
                  flex: isMobile ? "0 0 80%" : "0 0 60%",
                }}
              >
                <div className="carousel3__slide__card w-full">{child}</div>
              </div>
            );
          })}
        </div>
      </div>

      {!hideButtons && (
        <div className="flex w-full max-w-full items-center justify-center gap-[24px] px-4 sm:px-6 md:px-8 lg:px-10">
          <button
            onClick={scrollPrev}
            className="cursor-pointer transition-transform hover:scale-110"
            aria-label="Previous slide"
          >
            {left_navigation_icon ? (
              <div
                className={cn(
                  "w-[36px] h-[36px]",
                  "sm:w-[38px] sm:h-[38px]",
                  "md:w-[42px] md:h-[42px]",
                  "lg:w-[56px] lg:h-[56px]",
                  emblaApi?.canScrollPrev() ? "opacity-100" : "opacity-30"
                )}
              >
                <Image
                  src={left_navigation_icon || ""}
                  alt={left_navigation_icon_alt || "Previous"}
                  width={100}
                  height={100}
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
              <NavigationArrow
                className="xs:size-9 sm:size-[38px] lg:size-[52px] rotate-180"
                fill="#f74958"
                pathFill="#F8F9FA"
              />
            )}
          </button>
          <button
            onClick={scrollNext}
            className="cursor-pointer transition-transform hover:scale-110"
            aria-label="Next slide"
          >
            {right_navigation_icon ? (
              <div
                className={cn(
                  "w-[36px] h-[36px]",
                  "sm:w-[38px] sm:h-[38px]",
                  "md:w-[42px] md:h-[42px]",
                  "lg:w-[56px] lg:h-[56px]",
                  emblaApi?.canScrollNext() ? "opacity-100" : "opacity-30"
                )}
              >
                <Image
                  src={right_navigation_icon || ""}
                  alt={right_navigation_icon_alt || "Next"}
                  width={100}
                  height={100}
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
              <NavigationArrow
                className="xs:size-9 sm:size-[38px] lg:size-[52px]"
                fill="#f74958"
                pathFill="#F8F9FA"
              />
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default EmblaCarousel;
